/**
 * Low-data mode (SPEC_CAPTURE_UX_R2 §7.4): ON automatically on a slow connection (`navigator.connection.effectiveType`
 * slow-2g / 2g, or the OS "save data" flag), or by the manual toggle on the language screen. A manual choice wins over
 * the network signal and persists on this device (localStorage); the network signal is re-read on `change`.
 *
 * Effects in this cut: the map thumbnail is fetched only when the representative asks ("Show map"), the mosaic only on
 * expand (already so); audio / show-me are flags for the phase-3 engines. Nothing here touches capture or evidence.
 */

import { useCallback, useSyncExternalStore } from 'react';

export type LowDataSource = 'manual' | 'network' | 'off';
const STORAGE_KEY = 'vcip-svapp-lowdata';
const SLOW = new Set(['slow-2g', '2g']);

type Connection = { effectiveType?: string; saveData?: boolean; addEventListener?: (t: string, l: () => void) => void; removeEventListener?: (t: string, l: () => void) => void };

let manual: boolean | null = readManual();
const listeners = new Set<() => void>();
let wired = false;

function readManual(): boolean | null {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v === 'on' ? true : v === 'off' ? false : null;
  } catch {
    return null;
  }
}

function connection(): Connection | null {
  if (typeof navigator === 'undefined') return null;
  const n = navigator as Navigator & { connection?: Connection };
  return n.connection ?? null;
}

/** True when the network itself says "slow" — independent of the manual choice. */
export function networkSaysSlow(): boolean {
  const c = connection();
  if (!c) return false;
  return c.saveData === true || (typeof c.effectiveType === 'string' && SLOW.has(c.effectiveType));
}

export function lowDataSource(): LowDataSource {
  if (manual !== null) return manual ? 'manual' : 'off';
  return networkSaysSlow() ? 'network' : 'off';
}

export function isLowData(): boolean {
  return lowDataSource() !== 'off';
}

function notify(): void {
  for (const l of listeners) l();
}

/** The manual toggle; `null` clears it (back to the network signal). Persisted on this device. */
export function setLowDataManual(on: boolean | null): void {
  manual = on;
  try {
    if (on === null) window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, on ? 'on' : 'off');
  } catch {
    /* non-persistent is fine */
  }
  notify();
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  if (!wired) {
    wired = true;
    connection()?.addEventListener?.('change', notify);
  }
  return () => {
    listeners.delete(l);
  };
}

export function useLowData(): { readonly on: boolean; readonly source: LowDataSource; readonly setManual: (on: boolean | null) => void } {
  const source = useSyncExternalStore(subscribe, lowDataSource, lowDataSource);
  const setManual = useCallback((on: boolean | null) => setLowDataManual(on), []);
  return { on: source !== 'off', source, setManual };
}

export function resetLowDataForTests(): void {
  manual = null;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
  notify();
}
